// 8-bit indexed software surface, mirroring the original SDL 8bpp screen.
//
// Every draw operation in Meritous writes palette *indices* into an 8-bit
// framebuffer; a dynamic 256-colour palette (see palette.ts) is applied at
// present time. Reproducing that model exactly gives pixel-perfect parity with
// the original, including procedural effects (lasers, circuit arcs, shields).

import { rand } from '../core/rng';
import { RES_SCALE, SCREEN_W, SCREEN_H } from '../core/constants';
import { assertDrawContext } from './scene-guard';

type GpuBlitHook = (
  src: Surface,
  srcRect: Rect | null,
  dx: number,
  dy: number,
  nativeScale: number
) => void;
type GpuRectHook = (x: number, y: number, w: number, h: number, c: number) => void;
type GpuCircleHook = (x: number, y: number, r: number, c: number) => void;
type GpuCircleExHook = (x: number, y: number, r: number, r2: number, c: number) => void;

let gpuBlitHook: GpuBlitHook | null = null;
let gpuRectHook: GpuRectHook | null = null;
let gpuCircleHook: GpuCircleHook | null = null;
let gpuCircleExHook: GpuCircleExHook | null = null;

export function setGpuBlitHook(h: GpuBlitHook | null): void {
  gpuBlitHook = h;
}
export function setGpuRectHook(h: GpuRectHook | null): void {
  gpuRectHook = h;
}
export function setGpuCircleHook(h: GpuCircleHook | null): void {
  gpuCircleHook = h;
}
export function setGpuCircleExHook(h: GpuCircleExHook | null): void {
  gpuCircleExHook = h;
}

export class Surface {
  w: number;
  h: number;
  pix: Uint8Array; // palette indices
  colorKey: number; // -1 = none
  alpha: Uint8Array | null; // per-pixel: 0 = transparent, else opaque; null = fully opaque

  constructor(w: number, h: number, colorKey = -1, alpha: Uint8Array | null = null) {
    this.w = w;
    this.h = h;
    this.pix = new Uint8Array(w * h);
    this.colorKey = colorKey;
    this.alpha = alpha;
  }
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// The global screen surface (SCREEN_W x SCREEN_H).
export const screen = new Surface(SCREEN_W, SCREEN_H);

// Font data: 128 chars, 8x8, alpha 0..255. Accessor: fontAt(c, px, py).
export let font_data = new Uint8Array(128 * 8 * 8);

export function loadFontData(bytes: Uint8Array): void {
  // File order (levelblit.c text_init): for chr, for y, for x -> font_data[chr][x][y]
  const out = new Uint8Array(128 * 8 * 8);
  let k = 0;
  for (let chr = 0; chr < 128; chr++) {
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        out[chr * 64 + x * 8 + y] = bytes[k++];
      }
    }
  }
  font_data = out;
}

function fontAt(c: number, px: number, py: number): number {
  return font_data[(c & 127) * 64 + px * 8 + py];
}

const tr = Math.trunc;

// --- Rect fill (SDL_FillRect on the screen) -------------------------------

export function fillSurface(s: Surface, color: number): void {
  assertDrawContext();
  s.pix.fill(color & 255);
}

export function fillRectOn(s: Surface, rect: Rect, color: number): void {
  drawRectOn(s, rect.x, rect.y, rect.w, rect.h, color);
}

export function drawRectOn(
  s: Surface,
  x: number,
  y: number,
  w: number,
  h: number,
  c: number
): void {
  x = tr(x);
  y = tr(y);
  w = tr(w);
  h = tr(h);
  c &= 255;
  if (w <= 0 || h <= 0) return;
  let x0 = x;
  let y0 = y;
  let x1 = x + w;
  let y1 = y + h;
  if (x0 < 0) x0 = 0;
  if (y0 < 0) y0 = 0;
  if (x1 > s.w) x1 = s.w;
  if (y1 > s.h) y1 = s.h;
  if (x0 >= x1 || y0 >= y1) return;
  const pix = s.pix;
  for (let yy = y0; yy < y1; yy++) {
    let idx = yy * s.w + x0;
    for (let xx = x0; xx < x1; xx++) {
      pix[idx++] = c;
    }
  }
}

export function DrawRect(x: number, y: number, w: number, h: number, c: number): void {
  assertDrawContext();
  if (gpuRectHook) {
    gpuRectHook(x, y, w, h, c);
    return;
  }
  drawRectOn(screen, x, y, w, h, c);
}

// --- Circles (levelblit.c DrawCircle / DrawCircleEx) ----------------------

export function DrawCircle(x: number, y: number, r: number, c: number): void {
  assertDrawContext();
  if (gpuCircleHook) {
    gpuCircleHook(x, y, r, c);
    return;
  }
  x = tr(x);
  y = tr(y);
  r = tr(r);
  if (r < 1) return;
  for (let circ_y = 0; circ_y < r; circ_y++) {
    if (circ_y < r - 10) {
      const outer = tr(Math.sqrt(r * r - circ_y * circ_y));
      const inner = tr(Math.sqrt((r - 10) * (r - 10) - circ_y * circ_y));
      DrawRect(x - outer, y - circ_y, outer - inner, 1, c);
      DrawRect(x + inner, y - circ_y, outer - inner, 1, c);
      DrawRect(x - outer, y + circ_y, outer - inner, 1, c);
      DrawRect(x + inner, y + circ_y, outer - inner, 1, c);
    } else {
      const len = tr(Math.sqrt(r * r - circ_y * circ_y));
      DrawRect(x - len, y - circ_y, len * 2, 1, c);
      DrawRect(x - len, y + circ_y, len * 2, 1, c);
    }
  }
}

export function DrawCircleEx(x: number, y: number, r: number, r2: number, c: number): void {
  assertDrawContext();
  if (gpuCircleExHook) {
    gpuCircleExHook(x, y, r, r2, c);
    return;
  }
  x = tr(x);
  y = tr(y);
  r = tr(r);
  r2 = tr(r2);
  const diffi = r - r2;
  if (r < 1) return;
  for (let circ_y = 0; circ_y < r; circ_y++) {
    if (circ_y < r - diffi) {
      const outer = tr(Math.sqrt(r * r - circ_y * circ_y));
      const inner = tr(Math.sqrt((r - diffi) * (r - diffi) - circ_y * circ_y));
      DrawRect(x - outer, y - circ_y, outer - inner, 1, c);
      DrawRect(x + inner, y - circ_y, outer - inner, 1, c);
      DrawRect(x - outer, y + circ_y, outer - inner, 1, c);
      DrawRect(x + inner, y + circ_y, outer - inner, 1, c);
    } else {
      const len = tr(Math.sqrt(r * r - circ_y * circ_y));
      DrawRect(x - len, y - circ_y, len * 2, 1, c);
      DrawRect(x - len, y + circ_y, len * 2, 1, c);
    }
  }
}

// --- Lines & arcs (levelblit.c ThinLine / Arc) ----------------------------

function plotPixel(s: Surface, x: number, y: number, c: number): void {
  if (x >= 0 && y >= 0 && x < s.w && y < s.h) {
    s.pix[y * s.w + x] = c & 255;
  }
}

export function ThinLine(x1: number, y1: number, x2: number, y2: number, col: number): void {
  assertDrawContext();
  x1 = tr(x1);
  y1 = tr(y1);
  x2 = tr(x2);
  y2 = tr(y2);
  col &= 255;
  let dx = x2 - x1;
  let dy = y2 - y1;
  let dm = Math.abs(dx) > Math.abs(dy) ? dx : dy;
  if (dm === 0) return;
  if (dm < 0) {
    let t = x1;
    x1 = x2;
    x2 = t;
    t = y1;
    y1 = y2;
    y2 = t;
    dx = x2 - x1;
    dy = y2 - y1;
    dm = -dm;
  }
  if (dm === dx) {
    if (dy === 0) {
      DrawRect(x1, y1, x2 - x1 + 1, 1, col);
      return;
    }
    for (let i = 0; i < dm; i++) {
      const j = tr((dy * i) / dm);
      plotPixel(screen, i + x1, j + y1, col);
    }
  }
  if (dm === dy) {
    if (dx === 0) {
      DrawRect(x1, y1, 1, y2 - y1 + 1, col);
      return;
    }
    for (let i = 0; i < dm; i++) {
      const j = tr((dx * i) / dm);
      plotPixel(screen, j + x1, i + y1, col);
    }
  }
}

const M_PI = Math.PI;

export function Arc(x: number, y: number, r: number, dir: number): void {
  assertDrawContext();
  let bright = (rand() % 128) + 63;
  let i = 0;
  let l_x = x;
  let l_y = y;
  while (i < r) {
    i += (rand() % 5) + 25;
    const pdir = dir + ((rand() % 16) / 16.0) * 2.0 * (M_PI / 15.0);
    const ndir = dir - ((rand() % 16) / 16.0) * 2.0 * (M_PI / 15.0);
    const cdir =
      dir +
      ((rand() % 16) / 16.0) * 2.0 * (M_PI / 20.0) -
      ((rand() % 16) / 16.0) * 2.0 * (M_PI / 20.0);

    bright += rand() % 30;
    bright -= rand() % 30;
    if (bright < 0) bright = 0;
    if (bright > 255) bright = 255;

    const c1x = x + Math.cos(pdir) * i;
    const c1y = y + Math.sin(pdir) * i;
    ThinLine(l_x, l_y, c1x, c1y, bright);
    const c2x = x + Math.cos(ndir) * i;
    const c2y = y + Math.sin(ndir) * i;
    ThinLine(l_x, l_y, c2x, c2y, bright);

    for (let c = 0; c < 5; c++) {
      DrawRect(
        x + Math.cos(dir - M_PI / 10.0 + ((rand() % 16) / 16.0) * 2.0 * (M_PI / 10.0)) * i,
        y + Math.sin(dir - M_PI / 10.0 + ((rand() % 16) / 16.0) * 2.0 * (M_PI / 10.0)) * i,
        1,
        1,
        (rand() % 128) + 63
      );
    }

    i += (rand() % 5) + 25;
    const cx = x + Math.cos(cdir) * i;
    const cy = y + Math.sin(cdir) * i;
    ThinLine(c1x, c1y, cx, cy, bright);
    ThinLine(c2x, c2y, cx, cy, bright);
    l_x = cx;
    l_y = cy;
  }
}

// --- Text (levelblit.c draw_char / draw_text) -----------------------------

export function draw_char(cur_x: number, cur_y: number, c: number, tcol: number): void {
  assertDrawContext();
  draw_charOn(screen, cur_x, cur_y, c, tcol);
}

export function draw_charOn(s: Surface, cur_x: number, cur_y: number, c: number, tcol: number): void {
  cur_x = tr(cur_x);
  cur_y = tr(cur_y);
  tcol &= 255;
  const pix = s.pix;
  for (let py = 0; py < 8; py++) {
    for (let px = 0; px < 8; px++) {
      const fv = fontAt(c, px, py);
      if (fv === 0) continue;
      const ox = cur_x + px * RES_SCALE;
      const oy = cur_y + py * RES_SCALE;
      if (ox + RES_SCALE <= 0 || oy + RES_SCALE <= 0 || ox >= s.w || oy >= s.h) continue;
      for (let sy = 0; sy < RES_SCALE; sy++) {
        const ry = oy + sy;
        if (ry < 0 || ry >= s.h) continue;
        let dst = ry * s.w + ox;
        for (let sx = 0; sx < RES_SCALE; sx++) {
          const rx = ox + sx;
          if (rx < 0 || rx >= s.w) continue;
          if (fv === 255) {
            pix[dst] = tcol;
          } else {
            pix[dst] = tr((tcol * fv) / 256) + tr((pix[dst] * (256 - fv)) / 256);
          }
          dst++;
        }
      }
    }
  }
}

export function draw_text(x: number, y: number, str: string, tcol: number): void {
  assertDrawContext();
  let cur_x = tr(x);
  const startX = cur_x;
  let cur_y = tr(y);
  for (let n = 0; n < str.length; n++) {
    const c = str.charCodeAt(n);
    if (c === 10) {
      cur_x = startX;
      cur_y += 10 * RES_SCALE;
    } else {
      draw_char(cur_x, cur_y, c, tcol);
      cur_x += 8 * RES_SCALE;
    }
  }
}

export function draw_text_ex(x: number, y: number, str: string, tcol: number, srf: Surface): void {
  let cur_x = tr(x);
  const startX = cur_x;
  let cur_y = tr(y);
  tcol &= 255;
  for (let n = 0; n < str.length; n++) {
    const c = str.charCodeAt(n);
    if (c === 10) {
      cur_x = startX;
      cur_y += 8 * RES_SCALE;
    } else {
      for (let py = 0; py < 8; py++) {
        for (let px = 0; px < 8; px++) {
          if (!fontAt(c, px, py)) continue;
          const ox = px * RES_SCALE + cur_x;
          const oy = py * RES_SCALE + cur_y;
          for (let sy = 0; sy < RES_SCALE; sy++) {
            const ry = oy + sy;
            if (ry < 0 || ry >= srf.h) continue;
            for (let sx = 0; sx < RES_SCALE; sx++) {
              const rx = ox + sx;
              if (rx < 0 || rx >= srf.w) continue;
              srf.pix[ry * srf.w + rx] = tcol;
            }
          }
        }
      }
      cur_x += 8 * RES_SCALE;
    }
  }
}

// --- Blitting (SDL_BlitSurface) -------------------------------------------

// Copies src (optionally a sub-rect) onto dst at (dx, dy), honouring colour key
// and per-pixel alpha, with clipping. Straight index copy (both surfaces share
// the screen's greyscale logical palette in the original).
export function blit(
  src: Surface,
  srcRect: Rect | null,
  dst: Surface,
  dx: number,
  dy: number
): void {
  dx = tr(dx);
  dy = tr(dy);
  let sx = 0;
  let sy = 0;
  let sw = src.w;
  let sh = src.h;
  if (srcRect) {
    sx = tr(srcRect.x);
    sy = tr(srcRect.y);
    sw = tr(srcRect.w);
    sh = tr(srcRect.h);
  }

  // Clip source rect to source bounds.
  if (sx < 0) {
    sw += sx;
    dx -= sx;
    sx = 0;
  }
  if (sy < 0) {
    sh += sy;
    dy -= sy;
    sy = 0;
  }
  if (sx + sw > src.w) sw = src.w - sx;
  if (sy + sh > src.h) sh = src.h - sy;
  if (sw <= 0 || sh <= 0) return;

  // Clip against destination bounds.
  let ox = 0;
  let oy = 0;
  if (dx < 0) {
    ox = -dx;
  }
  if (dy < 0) {
    oy = -dy;
  }
  let cw = sw - ox;
  let ch = sh - oy;
  if (dx + sw > dst.w) cw -= dx + sw - dst.w;
  if (dy + sh > dst.h) ch -= dy + sh - dst.h;
  if (cw <= 0 || ch <= 0) return;

  const ck = src.colorKey;
  const sAlpha = src.alpha;
  const sPix = src.pix;
  const dPix = dst.pix;

  if (ck < 0 && sAlpha === null) {
    for (let row = 0; row < ch; row++) {
      const s0 = (sy + oy + row) * src.w + (sx + ox);
      const d0 = (dy + oy + row) * dst.w + (dx + ox);
      dPix.set(sPix.subarray(s0, s0 + cw), d0);
    }
    return;
  }

  for (let row = 0; row < ch; row++) {
    const srcRow = (sy + oy + row) * src.w + (sx + ox);
    const dstRow = (dy + oy + row) * dst.w + (dx + ox);
    for (let col = 0; col < cw; col++) {
      const sIdx = srcRow + col;
      if (sAlpha && sAlpha[sIdx] === 0) continue;
      const v = sPix[sIdx];
      if (ck >= 0 && v === ck) continue;
      dPix[dstRow + col] = v;
    }
  }
}

// Nearest-neighbour upscale blit: each source pixel becomes scale×scale on dst.
export function blitScaled(
  src: Surface,
  srcRect: Rect | null,
  dst: Surface,
  dx: number,
  dy: number,
  scale: number
): void {
  scale = tr(scale);
  if (scale <= 0) return;
  if (scale === 1) {
    blit(src, srcRect, dst, dx, dy);
    return;
  }

  dx = tr(dx);
  dy = tr(dy);
  let sx = 0;
  let sy = 0;
  let sw = src.w;
  let sh = src.h;
  if (srcRect) {
    sx = tr(srcRect.x);
    sy = tr(srcRect.y);
    sw = tr(srcRect.w);
    sh = tr(srcRect.h);
  }
  if (sw <= 0 || sh <= 0) return;

  const ck = src.colorKey;
  const sAlpha = src.alpha;
  const sPix = src.pix;
  const dPix = dst.pix;

  for (let row = 0; row < sh; row++) {
    const srcRow = (sy + row) * src.w + sx;
    for (let col = 0; col < sw; col++) {
      const sIdx = srcRow + col;
      if (sAlpha && sAlpha[sIdx] === 0) continue;
      const v = sPix[sIdx];
      if (ck >= 0 && v === ck) continue;

      const px = dx + col * scale;
      const py = dy + row * scale;
      for (let sy2 = 0; sy2 < scale; sy2++) {
        const dy2 = py + sy2;
        if (dy2 < 0 || dy2 >= dst.h) continue;
        const dstRow = dy2 * dst.w + px;
        for (let sx2 = 0; sx2 < scale; sx2++) {
          const dx2 = px + sx2;
          if (dx2 < 0 || dx2 >= dst.w) continue;
          dPix[dstRow + sx2] = v;
        }
      }
    }
  }
}

/** Blit with automatic upscale for 1x assets (nativeScale=1 → RES_SCALE on screen). */
export function blitGame(
  src: Surface,
  srcRect: Rect | null,
  dst: Surface,
  dx: number,
  dy: number,
  nativeScale = 1
): void {
  if (dst === screen) assertDrawContext();
  if (dst === screen && gpuBlitHook) {
    gpuBlitHook(src, srcRect, dx, dy, nativeScale);
    return;
  }
  const scale = RES_SCALE / nativeScale;
  if (scale === 1) {
    blit(src, srcRect, dst, dx, dy);
  } else {
    blitScaled(src, srcRect, dst, dx, dy, scale);
  }
}
